import React from 'react';

import Notification from '../notification';
import Table from '../table';
import Button from '../button';
import StationEditModal from './station-edit-modal';
import CTAContainer from '../cta-container';
import DeleteConfirmationModal from '../delete-confirmation-modal';

import { getStatusDisplayName } from '../station-profile-tab/station-status/helper';
import { COLORS } from '../../styles/variables';
import { isDiffProp, getOpenStatusName } from './helpers';

import styles from './styles.css';

const { api } = require('../../../lib/bputils').default;

export default function StationSuggestedEditsTable() {
  const [edits, setEdits] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [station, setStation] = React.useState(null);
  const [currentEdit, setCurrentEdit] = React.useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);

  React.useEffect(() => {
    const stationId = Number(window.location.pathname.split('/').slice(-1));
    getStationSuggestedEdits(stationId);
    getStationInfo(stationId);
  });

  React.useEffect(() => {
    if (loading) {
      if (edits && station) {
        setLoading(false);
      }
    }
  }, [edits, station]);

  const SUGGESTED_EDIT_TABLE_COLUMNS = [
    {
      title: 'Name',
      value: edit => edit.station.name,
      color: edit => (isDiffProp(station, edit.station, 'display_name', 'name') ? COLORS.starYellow : null),

    },
    {
      title: 'Address',
      value: edit => edit.station.address,
      color: edit => (isDiffProp(station.address, edit.station, 'address') ? COLORS.starYellow : null),
    },
    {
      title: 'Cross Street',
      value: edit => edit.station.cross_street,
      color: edit => (isDiffProp(station.address, edit.station, 'cross_street') ? COLORS.starYellow : null),
    },
    {
      title: 'City',
      value: edit => edit.station.city,
      color: edit => (isDiffProp(station.address, edit.station, 'city') ? COLORS.starYellow : null),
    },
    {
      title: 'State',
      value: edit => edit.station.state,
      color: edit => (isDiffProp(station.address, edit.station, 'state') ? COLORS.starYellow : null),
    },
    {
      title: 'Zip',
      value: edit => edit.station.postal_code,
      color: edit => (isDiffProp(station.address, edit.station, 'zip', 'postal_code') ? COLORS.starYellow : null),
    },
    {
      title: 'Lat',
      value: edit => edit.station.latitude,
      color: edit => (isDiffProp(station, edit.station, 'latitude') ? COLORS.starYellow : null),
    },
    {
      title: 'Lng',
      value: edit => edit.station.longitude,
      color: edit => (isDiffProp(station, edit.station, 'longitude') ? COLORS.starYellow : null),
    },
    {
      title: 'Status',
      value: edit => getStatusDisplayName(getOpenStatusName(edit.station.open_status)),
      color: edit => (getOpenStatusName(edit.station.open_status) !== station.open_status ? COLORS.starYellow : null),
    },
    {
      title: '',
      render: edit => <Button size="xs" secondary onClick={() => onCompareEditClick(edit)}>Compare Edit</Button>,
      colSpan: 2,
    },
  ];

  const onCompareEditClick = (edit) => {
    setCurrentEdit(edit);
  };

  const getStationInfo = async (stationId) => {
    await api.get({
      path: `/stations/${stationId}`,
      requestId: 'getStationDetails',
      success: (res) => {
        setStation(res);
      },
      error: () => Notification.error('Unable to retrieve the station info'),
    });
  };

  const getStationSuggestedEdits = async (stationId) => {
    await api.get({
      path: `/suggested-edits/stations/${stationId}`,
      requestId: 'getStationSuggestedEdits',
      success: (res) => {
        setEdits(res || []);
      },
      error: () => Notification.error('Unable to retrieve the suggested edits for this station'),
    });
  };

  const handleOnStationEditModalClose = () => {
    setCurrentEdit(null);
  };

  const deleteAllSuggestedEdits = async () => {
    const sId = station.id;
    setLoading(true);
    await api.put({
      path: `/suggested-edits/stations/${sId}/reject-all`,
      requestId: 'rejectAllEditsForStation',
      success: () => {
        Notification.success('The suggested edits were deleted successfully');
        setEdits([]);
      },
      error: () => Notification.error('Unable to delete the suggested edits'),
    });
    setLoading(false);
  };

  const showDeleteConfirmationModal = () => {
    setShowDeleteConfirmation(true);
  };

  const handleOnDelete = async () => {
    deleteAllSuggestedEdits();
    setShowDeleteConfirmation(false);
  };

  const handleOnEditSave = () => {
    setCurrentEdit(null);
  };

  return (
    <>
      <CTAContainer alignment="right" className={styles.deleteSuggestedEditsContainer}>
        <Button tertiary onClick={showDeleteConfirmationModal} disable={!station || edits?.length === 0}>Delete All Suggested Edits</Button>
      </CTAContainer>
      <Table
        loading={loading}
        tableColumns={SUGGESTED_EDIT_TABLE_COLUMNS}
        data={edits}
        loaderPadding={125}
        minWidth={1200}
      >
        {station && edits?.length ? (
          <tr className={styles.orginalStationRow}>
            <td>{station.display_name}</td>
            <td>{station.address.address}</td>
            <td>{station.address.cross_street}</td>
            <td>{station.address.city}</td>
            <td>{station.address.state}</td>
            <td>{station.address.zip}</td>
            <td>{station.latitude}</td>
            <td>{station.longitude}</td>
            <td>{getStatusDisplayName(station.open_status)}</td>
            <td colSpan={2}><div style={{ textAlign: 'center' }}>Current Station</div></td>
          </tr>
        ) : null}
        {station && !edits?.length ? <tr><td colSpan={11}><b>There are no pending edits for this station.</b></td></tr> : null}
      </Table>
      <StationEditModal edit={currentEdit} station={station} onClose={handleOnStationEditModalClose} onSave={handleOnEditSave} />
      <DeleteConfirmationModal
        show={showDeleteConfirmation}
        onDeleteConfirm={handleOnDelete}
        deleteButtonText="Delete All Suggested Edits"
        deleteConfirmationMessage="Are you sure you want to delete all of these suggested edits?"
      />
    </>
  );
}
